export class MiniEventRuntimeError extends Error {
    code;
    remediation;
    constructor(code, message, remediation) {
        super(`${code} ${message}`);
        this.name = 'MiniEventRuntimeError';
        this.code = code;
        this.remediation = remediation;
    }
}
function invalidInput(message, remediation) {
    return new MiniEventRuntimeError('WF7401', message, remediation);
}
function isRecord(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function snapshotValue(value, seen, label) {
    if (value === null
        || value === undefined
        || typeof value === 'string'
        || typeof value === 'boolean'
        || typeof value === 'number') {
        return value;
    }
    if (typeof value !== 'object') {
        throw invalidInput(`${label} contains an unsupported ${typeof value} value`, 'Pass only JSON-like values in event datasets, touches, and mark data.');
    }
    if (seen.has(value)) {
        throw invalidInput(`${label} contains a cycle`, 'Remove cyclic references before binding the value to event data.');
    }
    seen.add(value);
    try {
        if (Array.isArray(value)) {
            const clone = new Array(value.length);
            for (let index = 0; index < value.length; index += 1) {
                if (index in value) {
                    clone[index] = snapshotValue(value[index], seen, `${label}[${index}]`);
                }
            }
            return Object.freeze(clone);
        }
        const prototype = Object.getPrototypeOf(value);
        if (prototype !== Object.prototype && prototype !== null) {
            throw invalidInput(`${label} contains a non-plain object`, 'Normalize browser or component data to a plain object before event dispatch.');
        }
        const clone = {};
        for (const key of Reflect.ownKeys(value)) {
            const descriptor = Object.getOwnPropertyDescriptor(value, key);
            if (descriptor === undefined || !descriptor.enumerable) {
                continue;
            }
            if (typeof key !== 'string') {
                throw invalidInput(`${label} contains an enumerable symbol key`, 'Use only string keys in event datasets, touches, and mark data.');
            }
            if ('get' in descriptor || 'set' in descriptor) {
                throw invalidInput(`${label}.${key} is an accessor`, 'Replace accessor-backed event data with an explicit value before dispatch.');
            }
            Object.defineProperty(clone, key, {
                configurable: false,
                enumerable: true,
                value: snapshotValue(descriptor.value, seen, `${label}.${key}`),
                writable: false,
            });
        }
        return Object.freeze(clone);
    }
    finally {
        seen.delete(value);
    }
}
function snapshotRecord(value, label) {
    if (!isRecord(value)) {
        throw invalidInput(`${label} must be a plain record`, 'Pass an object containing the dataset values captured for this event target.');
    }
    return snapshotValue(value, new Set(), label);
}
function snapshotList(value, label) {
    if (value === undefined) {
        return Object.freeze([]);
    }
    if (!Array.isArray(value)) {
        throw invalidInput(`${label} must be an array`, 'Normalize component touch data to an array before event dispatch.');
    }
    return snapshotValue(value, new Set(), label);
}
function validateBinding(binding) {
    if (!isRecord(binding)
        || typeof binding.event !== 'string'
        || binding.event.length === 0
        || typeof binding.handler !== 'string'
        || binding.handler.length === 0
        || (binding.phase !== 'bubble' && binding.phase !== 'capture')
        || (binding.propagation !== 'continue' && binding.propagation !== 'stop')
        || typeof binding.mut !== 'boolean') {
        throw invalidInput('Mini Program event binding metadata is invalid', 'Generate a static event name, handler name, phase, propagation mode, and mut flag from WXML.');
    }
}
function targetId(value) {
    return isRecord(value) && typeof value.id === 'string' ? value.id : '';
}
function eventTarget(endpoint, dataset) {
    return Object.freeze({ id: targetId(endpoint), dataset });
}
export function dispatchMiniEvent(instance, binding, nativeEvent, datasetSnapshot) {
    validateBinding(binding);
    if (binding.mut) {
        throw new MiniEventRuntimeError('WF7403', `mut-bind event dispatch is not implemented: ${binding.event}`, 'Use bind/catch for the M2 subset or wait for logical custom-component propagation support.');
    }
    if (!isRecord(instance) || !isRecord(instance.methods)) {
        throw invalidInput('Mini Program event dispatch requires a Web page instance', 'Pass the page facade returned by createWebPage().createInstance().');
    }
    if (instance.lifecycleState === 'unloaded') {
        throw new MiniEventRuntimeError('WF7404', `Cannot dispatch ${binding.event} to an unloaded page`, 'Remove stale DOM listeners when unloading the page instance.');
    }
    const handler = instance.methods[binding.handler];
    if (typeof handler !== 'function') {
        throw new MiniEventRuntimeError('WF7402', `Page event handler is not available: ${binding.handler}`, 'Declare the referenced method on the Page option object or fix the WXML binding.');
    }
    if (!isRecord(nativeEvent)) {
        throw invalidInput('Native event input must be an object', 'Pass the browser or component event adapter result to dispatchMiniEvent.');
    }
    const datasets = snapshotRecord(datasetSnapshot, 'datasetSnapshot');
    if (!isRecord(datasets.target) || !isRecord(datasets.currentTarget)) {
        throw invalidInput('Event dataset snapshot must contain target and currentTarget records', 'Capture both target and currentTarget dataset objects when generating the event listener.');
    }
    const targetDataset = datasets.target;
    const currentTargetDataset = datasets.currentTarget;
    const timeStamp = typeof nativeEvent.timeStamp === 'number'
        && Number.isFinite(nativeEvent.timeStamp)
        && nativeEvent.timeStamp >= 0
        ? nativeEvent.timeStamp
        : 0;
    const event = Object.freeze({
        type: binding.event,
        timeStamp,
        target: eventTarget(nativeEvent.target, targetDataset),
        currentTarget: eventTarget(nativeEvent.currentTarget, currentTargetDataset),
        detail: nativeEvent.detail === undefined ? Object.freeze({}) : nativeEvent.detail,
        touches: snapshotList(nativeEvent.touches, 'nativeEvent.touches'),
        changedTouches: snapshotList(nativeEvent.changedTouches, 'nativeEvent.changedTouches'),
        mark: nativeEvent.mark === undefined
            ? Object.freeze({})
            : snapshotRecord(nativeEvent.mark, 'nativeEvent.mark'),
    });
    if (binding.propagation === 'stop') {
        if (typeof nativeEvent.stopPropagation !== 'function') {
            throw invalidInput(`catch event ${binding.event} cannot stop native propagation`, 'Provide a native event adapter with a callable stopPropagation method.');
        }
        nativeEvent.stopPropagation();
    }
    return handler(event);
}
//# sourceMappingURL=mini-event.js.map